import { useState, useEffect } from 'react';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';

interface PendingStatusResult {
  hasPendingProfileSubmission: boolean;
  hasPendingProfileUpdate: boolean;
  isLoading: boolean;
  pendingSubmissionDate?: string;
  pendingUpdateDate?: string;
}

export const useTutorPendingStatus = (): PendingStatusResult => {
  const { user } = useAuth();
  const [result, setResult] = useState<PendingStatusResult>({
    hasPendingProfileSubmission: false,
    hasPendingProfileUpdate: false,
    isLoading: false
  });

  useEffect(() => {
    const checkPendingStatus = async () => {
      if (!user || user.role !== 'tutor') {
        setResult({
          hasPendingProfileSubmission: false,
          hasPendingProfileUpdate: false,
          isLoading: false
        });
        return;
      }

      try {
        setResult(prev => ({ ...prev, isLoading: true }));
        
        // Check 1: Is the user still pending approval (initial profile submission)?
        let hasPendingProfileSubmission = false;
        let pendingSubmissionDate: string | undefined;
        
        if (user.status === 'pending') {
          hasPendingProfileSubmission = true;
          if (user.created) {
            pendingSubmissionDate = user.created;
          }
        }

        // Check 2: Are there any pending profile updates? - Single optimized query
        let hasPendingProfileUpdate = false;
        let pendingUpdateDate: string | undefined;

        try {
          // Single optimized query with OR condition
          const pendingUpdates = await pb.collection('tutor_pending_updates').getList(1, 1, {
            filter: `(tutor_id = "${user.id}" || tutor_email = "${user.email}") && status = "pending"`,
            sort: '-created'
          });

          if (pendingUpdates.items.length > 0) {
            hasPendingProfileUpdate = true;
            pendingUpdateDate = pendingUpdates.items[0].created;
          }
        } catch (error) {
          console.error('Error checking pending updates:', error);
        }

        // Check 3: Check profile_update_notifications for unread notifications  
        try {
          const notifications = await pb.collection('profile_update_notifications').getList(1, 10, {
            filter: `tutor_id = "${user.id}"`, // Remove is_read filter to see ALL notifications
            sort: '-created'
          });

          // Check ALL notifications, not just unread ones
          if (notifications.items.length > 0) {
            // Check if there's an active pending request by looking for:
            // 1. Unread notifications of type 'pending'  
            // 2. Recent notifications about submissions
            const hasPendingNotification = notifications.items.some(notif => {
              const isPendingType = notif.type === 'pending';
              const isUnreadPending = !notif.is_read && isPendingType;
              const hasSubmissionKeywords = notif.message && (
                notif.message.includes('pending') || 
                notif.message.includes('submitted') ||
                notif.message.includes('review') ||
                notif.message.includes('waiting')
              );
              
              return isUnreadPending || (isPendingType && hasSubmissionKeywords);
            });
            
            if (hasPendingNotification && !hasPendingProfileUpdate) {
              hasPendingProfileUpdate = true;
              pendingUpdateDate = notifications.items[0].created;
            }
          }
        } catch (error) {
          console.error('Error checking profile notifications:', error);
        }

        const finalResult = {
          hasPendingProfileSubmission,
          hasPendingProfileUpdate,
          isLoading: false,
          pendingSubmissionDate,
          pendingUpdateDate
        };

        setResult(finalResult);

      } catch (error) {
        console.error('Error checking pending status:', error);
        setResult({
          hasPendingProfileSubmission: false,
          hasPendingProfileUpdate: false,
          isLoading: false
        });
      }
    };

    checkPendingStatus();
  }, [user]);

  return result;
};